import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Users, Ticket, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';
import { istDateMed, istTime, istDate } from './utils/istDate';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyer, setBuyer] = useState({ name:'', email:'', phone:'', ticket_id:'', quantity:1 });
  const [creating, setCreating] = useState(false);
  
  // Enhanced validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  useEffect(() => {
    fetch(`/api/events/${id}`).then(r=>r.json()).then(res=>{
      if (res.success) { setEvent(res.event); setTickets(res.tickets||[]); }
    }).finally(()=>setLoading(false));
  }, [id]);

  // Validation function
  const validateField = (name, value) => {
    const errors = { ...validationErrors };
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          errors[name] = 'Name is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Name must be at least 2 characters';
        } else {
          delete errors[name];
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          errors[name] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errors[name] = 'Please enter a valid email address';
        } else {
          delete errors[name];
        }
        break;
        
      case 'phone':
        if (!value.trim()) {
          errors[name] = 'Phone number is required';
        } else if (!/^[6-9]\d{9}$/.test(value.replace(/\D/g, ''))) {
          errors[name] = 'Please enter a valid 10-digit Indian phone number';
        } else {
          delete errors[name];
        }
        break;
        
      case 'quantity':
        const qty = parseInt(value);
        if (!value || isNaN(qty) || qty < 1) {
          errors[name] = 'Quantity must be at least 1';
        } else if (qty > 10) {
          errors[name] = 'Maximum 10 tickets per order';
        } else {
          delete errors[name];
        }
        break;
        
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBuyer({ ...buyer, [name]: value });
    if (touchedFields[name]) {
      validateField(name, value);
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields({ ...touchedFields, [name]: true });
    validateField(name, value);
  };

  const checkout = async () => {
    // Mark all fields as touched
    const allFields = ['name', 'email', 'phone', 'quantity'];
    const newTouchedFields = {};
    allFields.forEach(field => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);
    
    // Validate all fields
    let isValid = true;
    allFields.forEach(field => {
      if (!validateField(field, buyer[field])) {
        isValid = false;
      }
    });
    
    if (!buyer.ticket_id) {
      alert('Please select a ticket');
      return;
    }
    
    if (!isValid) {
      alert('Please fix all validation errors before proceeding');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`/api/events/${id}/checkout`, { 
        method:'POST', 
        headers:{'Content-Type':'application/json'}, 
        body: JSON.stringify({ 
          ticket_id: buyer.ticket_id, 
          quantity: buyer.quantity, 
          buyer_name: buyer.name.trim(), 
          buyer_email: buyer.email.trim().toLowerCase(), 
          buyer_phone: buyer.phone.trim() 
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('Order confirmed! Check your email for ticket details.');
        // Reset form
        setBuyer({ name:'', email:'', phone:'', ticket_id:'', quantity:1 });
      } else {
        alert(data.message || 'Order failed. Please try again.');
      }
    } catch (error) {
      alert('Network error. Please check your connection and try again.');
    } finally { 
      setCreating(false);
    }  
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{background:'#F4ECDE'}}>
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{borderColor: '#471F21'}}></div>
        <div className="text-gray-600">Loading event...</div>
      </div>
    </div>
  );
  
  if (!event) return (
    <div className="min-h-screen flex items-center justify-center" style={{background:'#F4ECDE'}}>
      <div className="text-center">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Event Not Found</h3>
        <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or has been removed.</p>
        <button
          onClick={() => navigate('/events')}
          className="px-6 py-3 rounded-lg text-white font-medium transition-colors"
          style={{backgroundColor: '#471F21'}}
        >
          Back to Events
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{background:'#F4ECDE'}}>
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/10"></div>
      
      {/* Simplified Header for Embedding */}
      <div className="relative z-10">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              navigate('/events');
            }}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Events</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {/* Event Image */}
          {event.cover_image_url && (
            <div className="relative h-48 overflow-hidden">
              <img 
                src={event.cover_image_url} 
                alt={event.name} 
                className="w-full h-full object-cover" 
              />
            </div>
          )}
          
          {/* Event Content */}
          <div className="p-5">
            <h1 className="text-2xl font-bold mb-3" style={{color:'#471F21'}}>
              {event.name}
            </h1>
            
            <div className="flex items-center text-xs text-gray-600 mb-4">
              <Calendar className="w-3 h-3 mr-2" />
              <span>
                {istDateMed(event.start_at)}
                {' • '}
                {istTime(event.start_at)} - {istTime(event.end_at)}
              </span>
            </div>
            
            {event.description && (
              <p className="text-gray-700 text-sm leading-relaxed mb-5">
                {event.description}
              </p>
            )}
            
            <div className="border-t border-gray-200 pt-5">
              <h3 className="text-lg font-bold mb-3" style={{color:'#471F21'}}>Available Tickets</h3>
              {tickets.length===0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Ticket className="w-8 h-8 text-gray-400" />
                  </div>
                  <div className="text-gray-600 text-lg">No tickets available for this event.</div>
                </div>
              ):(
                <div className="space-y-3 mb-5">
                  {tickets.map(t=>{
                    const now = new Date();
                    const salesStart = t.sales_start_at ? new Date(t.sales_start_at) : null;
                    const salesEnd = t.sales_end_at ? new Date(t.sales_end_at) : null;
                    const isSalesActive = (!salesStart || salesStart <= now) && (!salesEnd || salesEnd >= now);
                    const available = (t.quantity_total || 0) - (t.quantity_sold || 0);
                    const isAvailable = available > 0 && isSalesActive;
                    
                    return (
                      <label key={t.id} className={`group relative flex items-center justify-between bg-gray-50 rounded-lg p-4 border transition-all duration-200 ${
                        !isAvailable 
                          ? 'opacity-50 cursor-not-allowed border-gray-200' 
                          : 'cursor-pointer hover:bg-gray-100 hover:border-gray-300 border-gray-200'
                      }`}>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-bold text-base" style={{color:'#471F21'}}>{t.name}</div>
                            <div className="text-lg font-bold" style={{color:'#471F21'}}>₹{t.price.toLocaleString()}</div>
                          </div>
                          <div className="flex items-center text-xs text-gray-600">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
                            <span>{available} left of {t.quantity_total}</span>
                          </div>
                          {!isSalesActive && (
                            <div className="mt-1 text-xs text-red-600">
                              {salesStart && salesStart > now ? 'Sales start: ' + istDate(salesStart) : ''}
                              {salesEnd && salesEnd < now ? 'Sales ended: ' + istDate(salesEnd) : ''}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <input 
                            type="radio" 
                            name="ticket" 
                            value={t.id} 
                            disabled={!isAvailable}
                            onChange={e=>setBuyer({...buyer, ticket_id:e.target.value})}
                            className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                          />
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Booking Form */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-base font-bold mb-3" style={{color:'#471F21'}}>Book Your Tickets</h4>
                
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  {/* Full Name */}
                  <div>
                    <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                      Full Name *
                      <div className="relative ml-1 group">
                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-40 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                          Enter your full name
                        </div>
                      </div>
                    </label>
                    <div className="relative">
                      <input 
                        name="name"
                        value={buyer.name}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        placeholder="Your name"
                        className={`w-full px-3 py-2 pr-8 rounded-lg border focus:ring-1 focus:outline-none text-sm transition-all ${
                          touchedFields.name
                            ? validationErrors.name
                              ? 'border-red-500 focus:ring-red-200'
                              : 'border-green-500 focus:ring-green-200'
                            : 'border-gray-300 focus:ring-gray-200'
                        }`}
                      />
                      {touchedFields.name && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          {validationErrors.name ? (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {touchedFields.name && validationErrors.name && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {validationErrors.name}
                      </p>
                    )}
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                      Email *
                      <div className="relative ml-1 group">
                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-40 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                          We'll send confirmation here
                        </div>
                      </div>
                    </label>
                    <div className="relative">
                      <input 
                        name="email"
                        type="email"
                        value={buyer.email}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        placeholder="Your email"
                        className={`w-full px-3 py-2 pr-8 rounded-lg border focus:ring-1 focus:outline-none text-sm transition-all ${
                          touchedFields.email
                            ? validationErrors.email
                              ? 'border-red-500 focus:ring-red-200'
                              : 'border-green-500 focus:ring-green-200'
                            : 'border-gray-300 focus:ring-gray-200'
                        }`}
                      />
                      {touchedFields.email && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          {validationErrors.email ? (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {touchedFields.email && validationErrors.email && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {validationErrors.email}
                      </p>
                    )}
                  </div>
                  
                  {/* Phone */}
                  <div>
                    <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                      Phone *
                      <div className="relative ml-1 group">
                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-40 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                          10-digit mobile number
                        </div>
                      </div>
                    </label>
                    <div className="relative">
                      <input 
                        name="phone"
                        type="tel"
                        value={buyer.phone}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        placeholder="Your phone"
                        className={`w-full px-3 py-2 pr-8 rounded-lg border focus:ring-1 focus:outline-none text-sm transition-all ${
                          touchedFields.phone
                            ? validationErrors.phone
                              ? 'border-red-500 focus:ring-red-200'
                              : 'border-green-500 focus:ring-green-200'
                            : 'border-gray-300 focus:ring-gray-200'
                        }`}
                      />
                      {touchedFields.phone && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          {validationErrors.phone ? (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {touchedFields.phone && validationErrors.phone && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {validationErrors.phone}
                      </p>
                    )}
                  </div>
                  
                  {/* Quantity */}
                  <div>
                    <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                      Quantity *
                      <div className="relative ml-1 group">
                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-40 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                          Max 10 tickets per order
                        </div>
                      </div>
                    </label>
                    <div className="relative">
                      <input 
                        name="quantity"
                        type="number"
                        min="1"
                        max="10"
                        value={buyer.quantity}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        placeholder="Tickets"
                        className={`w-full px-3 py-2 pr-8 rounded-lg border focus:ring-1 focus:outline-none text-sm transition-all ${
                          touchedFields.quantity
                            ? validationErrors.quantity
                              ? 'border-red-500 focus:ring-red-200'
                              : 'border-green-500 focus:ring-green-200'
                            : 'border-gray-300 focus:ring-gray-200'
                        }`}
                      />
                      {touchedFields.quantity && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          {validationErrors.quantity ? (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {touchedFields.quantity && validationErrors.quantity && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {validationErrors.quantity}
                      </p>
                    )}
                  </div>
                </div>
                
                <button 
                  disabled={creating} 
                  onClick={checkout} 
                  className="w-full py-2.5 px-5 rounded-lg font-semibold text-sm transition-all duration-300 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                  style={{ 
                    backgroundColor: '#471F21'
                  }}
                >
                  {creating ? 'Processing...' : 'Buy Tickets (Demo)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetails;


